import { useState } from "react";
import { api } from "../api";
import { ErrorState } from "../ui/State";
import type { Evidence, Packet, Result, StructureResult } from "./xero-history-import-types";
export function XeroSupplierCash({
  packet,
  structure,
  evidence,
  reason,
  root,
  busy,
  setBusy,
  stop,
}: {
  packet: Packet;
  structure: StructureResult | undefined;
  evidence: (checksum: string) => Omit<Evidence, "title">;
  reason: string;
  root: string;
  busy: boolean;
  setBusy: (value: boolean) => void;
  stop: { current: boolean };
}) {
  const items = packet.supplierCashMappings ?? [],
    [results, setResults] = useState<Record<string, Result>>({}),
    [page, setPage] = useState(0);
  async function copy() {
    if (!structure) return;
    setBusy(true);
    stop.current = false;
    try {
      for (const m of items) {
        if (stop.current) break;
        if (results[m.sourceId]?.canonicalId) continue;
        try {
          const event = packet.history.events.find((e) => e.sourceId === m.sourceId),
            date = (event?.lines[0] as { date?: string } | undefined)?.date,
            periods = structure.periods.filter((p) => date && p.startsOn <= date && p.endsOn >= date);
          if (!event || periods.length !== 1)
            throw Error("Prepare one original full-year period for this source date.");
          const ids = new Set([m.sourceId, m.creditSourceId]),
            { checksum, ...witness } = m.bankWitness;
          const result = await api<{ canonicalId: string }>(root + "/supplier-cash", {
            method: "POST",
            body: JSON.stringify({
              history: {
                organisation: packet.history.organisation,
                selector: packet.history.selector,
                buildingId: packet.history.buildingId,
                events: packet.history.events.filter((e) => ids.has(e.sourceId)),
                invoices: packet.history.invoices.filter((i) => ids.has(i.sourceId)),
                accountMappings: packet.history.accountMappings ?? [],
              },
              sourceId: m.sourceId,
              bankTransactionId: m.bankTransactionId,
              ...(m.creditSourceId ? { creditSourceId: m.creditSourceId } : {}),
              scheduleId: structure.scheduleId,
              periodId: periods[0]!.id,
              evidence: evidence(m.sourceChecksum),
              bankWitness: { ...witness, evidence: evidence(checksum) },
              reason,
            }),
          });
          setResults((r) => ({ ...r, [m.sourceId]: result }));
        } catch (error) {
          setResults((r) => ({ ...r, [m.sourceId]: { error } }));
        }
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-label="Original supplier refunds and overpayments">
      <h2>Original supplier refunds and overpayments</h2>
      <p>
        {items.length} original cash records · {Object.values(results).filter((r) => r.canonicalId).length} confirmed in
        this session.
      </p>
      <p>
        Original overpayments remain supplier credits with their own source records. Refunds settle the exact original
        credit against existing bank cash.
      </p>
      <button disabled={busy || !structure || reason.trim().length < 5} onClick={() => void copy()}>
        Copy original supplier refunds and overpayments
      </button>
      <table>
        <thead>
          <tr>
            <th>Original cash</th>
            <th>Kind</th>
            <th>Import result</th>
          </tr>
        </thead>
        <tbody>
          {items.slice(page * 25, (page + 1) * 25).map((m) => (
            <tr key={m.sourceId}>
              <td>{m.sourceId}</td>
              <td>{m.creditSourceId ? "Credit-note refund" : "Supplier overpayment"}</td>
              <td>
                {results[m.sourceId]?.canonicalId ? (
                  "Copied"
                ) : results[m.sourceId]?.error ? (
                  <ErrorState error={results[m.sourceId]?.error} />
                ) : (
                  "Ready for source checks"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length > 25 && (
        <nav aria-label="Original supplier cash pages">
          <button disabled={!page} onClick={() => setPage(page - 1)}>
            Previous
          </button>{" "}
          Page {page + 1} of {Math.ceil(items.length / 25)}{" "}
          <button disabled={(page + 1) * 25 >= items.length} onClick={() => setPage(page + 1)}>
            Next
          </button>
        </nav>
      )}
    </section>
  );
}
